/**
 * @fileoverview CSV uploader component for Universal Transactions (react-dropzone based).
 * @module features/universal-transactions/components/upload/CsvUploader
 */

import { useCallback, useState } from 'react'
import type { ReactElement } from 'react'
import { useDropzone, type FileRejection } from 'react-dropzone'
import { Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'

export interface CsvUploaderProps {
  onFileSelect: (file: File) => void
  isLoading?: boolean
  maxSizeBytes?: number
}

/**
 * CsvUploader renders a drag-and-drop zone and validates CSV file type and size.
 *
 * @component
 */
export function CsvUploader({
  onFileSelect,
  isLoading = false,
  maxSizeBytes = 10 * 1024 * 1024,
}: CsvUploaderProps): ReactElement {
  const { t } = useTranslation('features-universal-transactions')
  const [error, setError] = useState<string | null>(null)

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        const first = fileRejections[0]
        const msg =
          first?.errors?.[0]?.message ??
          t('uploader.error.invalidFile', { defaultValue: 'Invalid file' })
        setError(msg)
        return
      }
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0]!
        if (!file.name.toLowerCase().endsWith('.csv')) {
          setError(t('uploader.error.type', { defaultValue: 'Please upload a CSV file (.csv)' }))
          return
        }
        if (file.size > maxSizeBytes) {
          setError(t('uploader.error.size', { defaultValue: 'File size must be ≤ 10MB' }))
          return
        }
        setError(null)
        onFileSelect(file)
      }
    },
    [onFileSelect, maxSizeBytes, t]
  )

  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
    },
    multiple: false,
    disabled: isLoading,
    maxSize: maxSizeBytes,
  })

  const selected = acceptedFiles[0]

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-5 gap-4">
        {/* Requirements explainer - 20% */}
        <div className="col-span-1 space-y-2 rounded-md bg-muted/50 p-3 h-fit">
          <p className="text-xs font-medium text-foreground">
            {t('uploader.requirements.heading', { defaultValue: 'Required columns:' })}
          </p>
          <ul className="space-y-1 text-xs text-muted-foreground list-disc list-inside">
            <li>{t('uploader.requirements.transactionDate', { defaultValue: 'Transaction date*' })}</li>
            <li>{t('uploader.requirements.amount', { defaultValue: 'Amount*' })}</li>
            <li>{t('uploader.requirements.currency', { defaultValue: 'Currency*' })}</li>
            <li>{t('uploader.requirements.titleField', { defaultValue: 'Title*' })}</li>
            <li>{t('uploader.requirements.counterparty', { defaultValue: 'Counterparty (optional, but recommended)' })}</li>
          </ul>
        </div>

        {/* Upload dropzone - 80% */}
        <div className="col-span-4 flex flex-col">
          <div
            {...getRootProps({
              role: 'button',
              'aria-label': t('uploader.aria.dropzone', { defaultValue: 'CSV file uploader dropzone' }),
            })}
            className={
              'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors duration-200 flex-1 flex flex-col items-center justify-center ' +
              (isDragActive
                ? 'border-primary/60 bg-accent/30 '
                : 'border-border hover:border-primary/40 ') +
              (isLoading ? 'opacity-50 cursor-not-allowed ' : '')
            }
          >
            <input
              {...getInputProps({
                'aria-label': t('uploader.aria.input', { defaultValue: 'CSV file input' }),
              })}
            />
            <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
            {isDragActive ? (
              <p className="text-sm font-medium">
                {t('uploader.drop.active', { defaultValue: 'Drop the CSV file here…' })}
              </p>
            ) : (
              <>
                <p className="text-sm font-medium mb-1">
                  {t('uploader.drop.title', { defaultValue: 'Drag & drop your CSV file' })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('uploader.drop.subtitle', { defaultValue: 'or click to browse (max 10MB)' })}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-4">
        {/* Info line 1 - 20% */}
        <div className="col-span-1 text-xs text-muted-foreground text-left">
          <p>{t('uploader.requirements.infoLine1', { defaultValue: 'All other columns will be ignored.' })}</p>
        </div>

        {/* Info line 2 - 80% */}
        <div className="col-span-4 text-xs text-muted-foreground flex items-center justify-center">
          <p>{t('uploader.requirements.infoLine2', { defaultValue: 'Your header column names may be different, simply match in the next step.' })}</p>
        </div>
      </div>

      {selected && (
        <div className="flex items-center gap-2 p-2 bg-accent/40 rounded-md">
          <span className="text-sm font-medium">{selected.name}</span>
          <span className="text-xs text-muted-foreground">
            ({(selected.size / 1024).toFixed(1)} KB)
          </span>
        </div>
      )}

      {error && (
        <div role="alert" className="text-sm text-destructive">
          {error}
        </div>
      )}

      {selected && (
        <div className="pt-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-label={t('uploader.aria.useSelectedFile', { defaultValue: 'Use selected file' })}
            disabled={isLoading}
            onClick={() => onFileSelect(selected)}
          >
            {t('uploader.button.useThisFile', { defaultValue: 'Use this file' })}
          </Button>
        </div>
      )}
    </div>
  )
}

export default CsvUploader
